import { useCallback, useEffect, useRef, useState } from 'react'

import { useReactiveVar } from '@apollo/client'
import {
  Drawer,
  IconButton,
  Paragraph,
  SmallGammaBadge,
  Tooltip,
  typography,
} from '@joor/design-system'
import { useSelector } from 'react-redux'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components/macro'

import { OrderStatus } from '__generated__/atlas-types'

import { formatDate } from 'utils/dates'
import { pluralize } from 'utils/formatters'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { fromGlobalId } from 'utils/transformations/graphql'

import {
  getDateFormat,
  isAccountTypeBrand,
  isLiteRetailer as isLiteRetailerAccount,
} from 'store/currentUser/selectors'

import { ORDER_LIST, SHOPIFY_RETAILER_SYNC_CONFIG } from 'routes/paths'

import { Header, MessagesDrawer, Overview, PayView } from './components'
import { DownloadDrawer } from './components/DownloadDrawer/DownloadDrawer'
import SendPaymentRequestActionButton from './components/SendPaymentRequestActionButton/SendPaymentRequestActionButton'
import AssignRetailerModal from './modals/AssignRetailerModal/AssignRetailerModal'
import { ProductsEditModal } from './modals/ProductsEditModal/ProductsEditModal'
import { AddProductsNonVisible } from './modals/ProductsEditModal/components/AddProductsNonVisible'
import { useJoorPay, useTabRedirections } from './review.hooks'
import { REVIEW_PAGE_TEST_IDS, ReviewPageIds } from './review.ids'
import {
  OrderErrors,
  useFullOrderInfo,
  useOrderHasRemainingBalance,
  useShopifySubscriptionStatus,
} from './review.queries'
import {
  orderReviewDrawerVar,
  orderReviewPartialErrorVar,
} from './review.store'
import { OrderReviewDrawers } from './review.types'
import {
  getOrderSeparatedComments,
  isLinesheetVisibleForRetailer,
} from './utils/review.utils'
import { CTABanner } from 'features/Contentful/CTABanner/CTABanner'
import { VisibleInFieldsValues } from 'features/Contentful/CTABanner/constants'
import { OrdersShareModal } from 'features/Orders/Modals'
import { useSyncShopifyOrderStyles } from 'features/ShopifyIntegration/orderStyles.hooks'
import { VisualAssortmentButton } from 'features/VisualAssortment/components/VisualAssortmentButton/VisualAssortmentButton'
import { useShouldOfferVA } from 'features/VisualAssortment/hooks/useShouldOfferVA'

const StyledParagraph = styled(Paragraph)`
  text-align: center !important;
`

const LastUpdatedInfo = styled.div`
  ${typography.alpha.primaryActive.caption}
  font-weight: var(--font-weight-regular);
  color: var(--color-primary-800);
  font-style: italic;
  align-self: end;
`

/*
 * The design-system <Tooltip> is not portalled: it renders the bubble as a
 * sibling of its trigger and positions it with floating-ui in 'absolute'
 * strategy, i.e. against the nearest positioned ancestor. Without the
 * 'position: relative' below that ancestor is <Container>, which does not move
 * when <CTABanner> mounts late (two chained Contentful fetches, held in the
 * banner's own state so this page never re-renders) and pushes the whole header
 * down. floating-ui does not always re-measure in time — its move detector
 * bails out entirely when the trigger is not measurable at attach time — so the
 * stored coordinates end up short by exactly the banner height and the bubble
 * lands on top of the icons, where it swallows the click because it is
 * 'pointer-events: auto' while visible.
 *
 * Making the icon row the positioned ancestor means it shifts together with the
 * buttons, so stale coordinates stay correct and the banner is invisible to the
 * tooltips. Covers every tooltip in this row without touching their call sites.
 * 'position: relative' with 'z-index: auto' creates no stacking context, and
 * this element has no 'overflow', so nothing is repainted or clipped.
 * TODO: remove once <Tooltip> is portalled upstream in @joor/design-system.
 */
const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  background: var(--color-secondary-300);
  border-radius: 15px;

  & > :first-child > * {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    padding-left: 12px;
  }

  & > :last-child > * {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    padding-right: 12px;
  }

  & button {
    background: var(--color-secondary-300);
    border: 0;
    border-radius: 0px;
    padding: 0 8px;

    &.highlight {
      border-radius: 10px;
      background: var(--color-secondary-400);
    }

    & svg {
      --main-color: var(--color-primary-500);
    }

    &:not(:disabled):hover {
      background: var(--color-secondary-300);
      border: 0;
      box-shadow: none;
    }

    &:disabled {
      background: var(--color-secondary-100);
      border: 0;
      svg {
          --main-color: var(--color-primary-300);
      }

      &:hover {
        cursor: not-allowed;
        svg {
          --main-color: var(--color-primary-300);
      }
    }
  }
`

const FakeStickyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const FakeStickyScrollableContent = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`

type ShopifyIntegrationActionProps = {
  id?: string
  currencyCode?: string
}

const ShopifyIntegrationAction = ({
  id,
  currencyCode,
}: ShopifyIntegrationActionProps) => {
  const { isSubscriptionActive, shopifyStoreCurrencyCode } =
    useShopifySubscriptionStatus()
  const { syncShopifyOrderStyles, syncStarted } = useSyncShopifyOrderStyles()

  const getTooltipText = () => {
    if (!isSubscriptionActive) {
      return (
        <>
          <Paragraph type="3">Product Sync not activated.</Paragraph>
          <StyledParagraph type="3">
            See{' '}
            <Link
              to={SHOPIFY_RETAILER_SYNC_CONFIG}
              target="_blank"
              style={{ color: 'var(--color-secondary-800)' }}
            >
              Integration Settings
            </Link>
            .
          </StyledParagraph>
        </>
      )
    }

    if (shopifyStoreCurrencyCode !== currencyCode) {
      return (
        <>
          <Paragraph type="3">
            JOOR Price Type does not match Shopify currency.
          </Paragraph>
          <StyledParagraph type="3">Prices will not sync.</StyledParagraph>
        </>
      )
    }

    return 'Sync products to Shopify'
  }

  const tooltipText = getTooltipText()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Tooltip content={tooltipText} defaultPlacement="bottom">
        <IconButton
          id={ReviewPageIds.ShopifySyncActionId}
          iconName="sync"
          onClick={() => (id ? syncShopifyOrderStyles([id]) : undefined)}
          data-testid={ReviewPageIds.ShopifySyncActionId}
          disabled={!(isSubscriptionActive && !syncStarted)}
        />
      </Tooltip>
    </div>
  )
}
const OrderReview = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    orderFullInfo,
    loading,
    refetch,
    error,
    products,
    productAmounts,
    fetchAllProducts,
  } = useFullOrderInfo()
  const { hasRemainingBalance } = useOrderHasRemainingBalance()
  const { trackEvent } = useUserAnalytics()
  const tabRedirections = useTabRedirections({
    webOrderLookupId: orderFullInfo?.webOrderLookupId,
    orderId: orderFullInfo?.id,
    orderStatus: orderFullInfo?.status || undefined,
    orderInfoLoading: loading,
    isOrphanOrder: orderFullInfo?.isOrphan,
  })
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false)
  const [isAddProductsModalOpen, setIsAddProductsModalOpen] = useState(false)
  const [hasUserScrolled, setHasUserScrolled] = useState(false)
  const shouldShowReloadProducts = useRef(false)

  const { isJoorPayEnabled } = useJoorPay(orderFullInfo?.brand?.id)

  const isBrand = useSelector(isAccountTypeBrand)
  const isLiteRetailer = useSelector(isLiteRetailerAccount)
  const shouldOfferVA = useShouldOfferVA()
  const dateFormat = useSelector(getDateFormat)

  const [assignRetailerModalOpen, setAssignRetailerModalOpen] = useState(false)
  const [isShareModalOpen, setShareModalOpen] = useState(false)

  const isLinesheetVisible = isLinesheetVisibleForRetailer({
    collections: orderFullInfo?.collections,
    retailerProfile: orderFullInfo?.retailerProfile,
  }) // this method can be replaced with the visibleToViewer field in the orders query
  const isShareEnabled =
    orderFullInfo?.status === OrderStatus.NOTES ||
    orderFullInfo?.status === OrderStatus.PENDING ||
    orderFullInfo?.status === OrderStatus.APPROVED

  const isShopifyIntegrationOptionVisible =
    isLiteRetailer &&
    (orderFullInfo?.status === OrderStatus.APPROVED ||
      orderFullInfo?.status === OrderStatus.SHIPPED)

  useEffect(() => {
    if (error === OrderErrors.ORDER_HAS_PARTIAL_ERROR) {
      orderReviewPartialErrorVar({
        priceType: true,
      })
    }
  }, [error])

  const replaceUrlTab = useCallback(
    (tab: OrderReviewTab) => {
      navigate(
        {
          pathname: location.pathname,
          search: new URLSearchParams({ tab }).toString(),
        },
        {
          replace: true,
        },
      )
    },
    [navigate, location.pathname],
  )
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(location.search).get('tab')
    return isOrderReviewTab(tab) ? tab : 'overview'
  })

  // if URL has no tab or invalid tab, replace it with `overview`
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab')
    if (!isOrderReviewTab(tab)) {
      replaceUrlTab('overview')
    }
  }, [location.search]) // run on first mount and when URL changes

  const drawer = useReactiveVar(orderReviewDrawerVar)

  const handleToggleDrawer = (newDrawer: OrderReviewDrawers) => {
    orderReviewDrawerVar(drawer === newDrawer ? null : newDrawer)
  }

  const handleClickTab = (tab: 'overview' | 'pay') => {
    if (drawer === OrderReviewDrawers.Casepack) {
      orderReviewDrawerVar(null)
    }
    replaceUrlTab(tab)
    setActiveTab(tab)
  }

  const tabComponents = {
    overview: (
      <>
        <Overview
          isJoorPayEnabled={isJoorPayEnabled}
          setIsProductsModalOpen={() => setIsProductsModalOpen(true)}
          loading={loading}
          refetch={refetch}
          orderData={orderFullInfo}
          products={products}
          productAmounts={productAmounts}
          fetchAllProducts={fetchAllProducts}
        />
      </>
    ),
    pay: (
      <>
        <PayView
          orderId={orderFullInfo?.id}
          brandId={orderFullInfo?.brand?.id}
          brandName={orderFullInfo?.brand?.name}
          loading={tabRedirections.pay.loading}
          isOrphanOrder={orderFullInfo?.isOrphan}
          openModal={() => setAssignRetailerModalOpen(true)}
        />
      </>
    ),
  }

  const content = tabComponents[activeTab]

  const { id: decodedWebId } = fromGlobalId(orderFullInfo?.webOrderLookupId)

  const commentsCount = getOrderSeparatedComments(
    orderFullInfo?.orderComments,
  ).filter((comment) => comment.message !== '\n').length

  const commentsTooltipLabel =
    commentsCount === 0
      ? 'Comments'
      : `View ${pluralize(commentsCount, 'Comment')}`

  const showSendPaymentRequestButton = isBrand && isJoorPayEnabled

  return (
    <Container>
      {error === OrderErrors.ORDER_DOESNT_EXIST && (
        <Navigate
          to={ORDER_LIST}
          state={{
            toastMessage: {
              type: 'info',
              title: 'Order not found',
              description: 'Please ensure that you have the correct URL.',
            },
          }}
        />
      )}
      <BannerSlot>
        {shouldOfferVA && (
          <CTABanner visibleIn={VisibleInFieldsValues.OrderReview} />
        )}
      </BannerSlot>
      <HeaderContainer>
        <NavbarContainer>
          <FakeTabsWrapper>
            <FakeTab
              id="sub-navbar-tab-overview"
              className={activeTab === 'overview' ? 'active' : ''}
              onClick={() => handleClickTab('overview')}
            >
              Overview
            </FakeTab>
            <FakeTab
              id="sub-navbar-tab-pay"
              className={activeTab === 'pay' ? 'active' : ''}
              onClick={() => handleClickTab('pay')}
            >
              Pay
            </FakeTab>
          </FakeTabsWrapper>
          <ButtonsContainer>
            <LastUpdatedInfo>
              {`Last updated ${formatDate(
                orderFullInfo?.modified,
                dateFormat,
                false,
              )} ${
                orderFullInfo?.lastModifier?.email
                  ? ` by ${orderFullInfo.lastModifier.email}`
                  : ``
              }`}
            </LastUpdatedInfo>
            <IconWrapper>
              {showSendPaymentRequestButton && (
                <SendPaymentRequestActionButton
                  disabled={
                    orderFullInfo?.status === OrderStatus.CANCELLED ||
                    !hasRemainingBalance ||
                    orderFullInfo?.isOrphan
                  }
                  isOrphan={orderFullInfo?.isOrphan}
                  hasRemainingBalance={hasRemainingBalance}
                />
              )}
              <Tooltip defaultPlacement="bottom" content="Share Order">
                <IconButton
                  id={ReviewPageIds.ShareId}
                  iconName="group"
                  onClick={(): void => {
                    setShareModalOpen((state) => !state)
                    trackEvent('Order_Review_Share_Button')
                  }}
                  data-testid={REVIEW_PAGE_TEST_IDS.ShareToggle}
                  disabled={!isShareEnabled}
                />
              </Tooltip>
              {isLiteRetailer && (
                <VisualAssortmentButton
                  id={ReviewPageIds.VisualAssortmentId}
                  orderId={orderFullInfo?.id}
                  orderStatus={orderFullInfo?.status ?? undefined}
                  productsCount={orderFullInfo?.products?.totalProducts ?? 0}
                />
              )}
              {isShopifyIntegrationOptionVisible && (
                <ShopifyIntegrationAction
                  id={orderFullInfo?.id}
                  currencyCode={orderFullInfo?.priceType?.currency.code}
                />
              )}
              <Tooltip defaultPlacement="bottom" content={commentsTooltipLabel}>
                <div style={{ position: 'relative' }}>
                  <IconButton
                    id={ReviewPageIds.CommentsId}
                    className={
                      drawer === OrderReviewDrawers.Messages ? 'highlight' : ''
                    }
                    iconName="chat"
                    onClick={() => {
                      handleToggleDrawer(OrderReviewDrawers.Messages)
                      trackEvent('Order_Review_Messages_Button')
                    }}
                    data-testid={REVIEW_PAGE_TEST_IDS.MessagesToggle}
                  />
                  {!!commentsCount && <StyledBadge value={commentsCount} />}
                </div>
              </Tooltip>
              <Tooltip defaultPlacement="bottom" content="Download">
                <IconButton
                  id={ReviewPageIds.DownloadId}
                  className={
                    drawer === OrderReviewDrawers.Download ? 'highlight' : ''
                  }
                  iconName="download"
                  onClick={() => {
                    handleToggleDrawer(OrderReviewDrawers.Download)
                    trackEvent('Order_Review_Download_Button')
                  }}
                  data-testid={REVIEW_PAGE_TEST_IDS.DownloadToggle}
                />
              </Tooltip>
            </IconWrapper>
          </ButtonsContainer>
        </NavbarContainer>
        <hr style={{ border: '1px solid var(--color-primary-100)' }} />
      </HeaderContainer>
      <Drawer.Wrapper>
        <Drawer
          isOpen={!!drawer}
          onClose={() => {
            orderReviewDrawerVar(null)
            trackEvent(`Order_Review_${drawer}_Modal_(Close)`)
          }}
        >
          {drawer === OrderReviewDrawers.Messages && (
            <MessagesDrawer
              id={orderFullInfo?.id}
              status={orderFullInfo?.status || undefined}
              retailerProfile={orderFullInfo?.retailerProfile || undefined}
              dataMessages={orderFullInfo?.orderComments || ''}
              loading={loading}
            />
          )}
          {drawer === OrderReviewDrawers.Download && (
            <DownloadDrawer
              isLinesheetVisible={isLinesheetVisible}
              decodedWebOrderId={decodedWebId ?? ''}
              brandId={orderFullInfo?.brand?.id}
              onClose={() => {
                orderReviewDrawerVar(null)
                trackEvent('Order_Review_Download_Modal_(Close)')
              }}
            />
          )}
        </Drawer>
        <FakeStickyWrapper>
          <Header
            order={orderFullInfo}
            error={error}
            data-testid={REVIEW_PAGE_TEST_IDS.OrderHeader}
            openAssignRetailerModal={() => setAssignRetailerModalOpen(true)}
            isFetching={loading}
            showShadow={hasUserScrolled}
          />
          <FakeStickyScrollableContent
            onScroll={(event) => {
              if (event.currentTarget.scrollTop > 0 && !hasUserScrolled) {
                setHasUserScrolled(true)
              } else if (
                event.currentTarget.scrollTop === 0 &&
                hasUserScrolled
              ) {
                setHasUserScrolled(false)
              }
            }}
          >
            {content}
          </FakeStickyScrollableContent>
        </FakeStickyWrapper>
      </Drawer.Wrapper>
      {assignRetailerModalOpen && (
        <AssignRetailerModal
          buyerEmail={orderFullInfo?.buyer?.email ?? ''}
          onCloseModal={() => setAssignRetailerModalOpen(false)}
        />
      )}
      {!!orderFullInfo && isShareModalOpen && (
        <OrdersShareModal
          title={`Share Order #${orderFullInfo?.referenceNumber}`}
          isOpen={isShareModalOpen}
          setOpen={setShareModalOpen}
          orderIds={[orderFullInfo?.id || '']}
        />
      )}
      {isProductsModalOpen && (
        <ProductsEditModal
          setIsProductsModalOpen={setIsProductsModalOpen}
          setIsAddProductsModalOpen={setIsAddProductsModalOpen}
          shouldShowReloadProducts={shouldShowReloadProducts}
          {...tabRedirections.products}
        />
      )}
      {isAddProductsModalOpen && (
        <AddProductsNonVisible
          order={orderFullInfo}
          onCloseModal={() => {
            setIsAddProductsModalOpen(false)
            shouldShowReloadProducts.current = true
            // Re-opening the modal with the PHP page needs some time for getting the updated products
            // from Add Products Modal
            setTimeout(() => {
              setIsProductsModalOpen(true)
            }, 1000)
          }}
        />
      )}
    </Container>
  )
}

const ORDER_REVIEW_TABS = ['overview', 'pay'] as const
type OrderReviewTab = (typeof ORDER_REVIEW_TABS)[number]
const isOrderReviewTab = (tab?: string | null): tab is OrderReviewTab =>
  ORDER_REVIEW_TABS.includes(tab as OrderReviewTab)

const StyledBadge = styled(SmallGammaBadge)`
  position: absolute;
  top: 0;
  right: 0;
  background-color: var(--color-secondary-800);
`

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--actual-navbar-height));
  margin-top: var(--actual-navbar-top-displacement);

  /*
   * sc-257585: prevent the closed drawer from making the page horizontally
   * scrollable. The design-system <Drawer> panel is always mounted; when
   * closed on the right it is parked off-screen via 'margin-right: -320px'
   * (this is what drives its open/close slide animation). Its parent
   * Drawer.Wrapper uses 'overflow: hidden', which hides the parked panel
   * visually but leaves the wrapper programmatically scrollable — so its
   * scrollWidth exceeds clientWidth by 320px and Cypress actionability
   * auto-scroll (scrollBehavior: "center") shifts the whole page, pushing
   * controls off-screen. 'overflow: clip' clips the parked panel exactly like
   * 'hidden' but is not a scroll container, so the wrapper can no longer be
   * scrolled. The drawer panel is left untouched, so its slide animation is
   * preserved. Drawer.Wrapper is selected via its child .drawer panel because
   * the DS wrapper does not forward a className.
   *
   * 'min-height: 0' is required alongside 'overflow: clip': unlike
   * 'overflow: hidden', 'clip' does not make the element a scroll container, so
   * it would not give this flex item the automatic 'min-height: 0' that the DS
   * relied on. Without it the wrapper grows to fit tall drawer content, the
   * drawer's own content stops scrolling internally, and its footer (the
   * Download button) is pushed below the viewport. Setting it explicitly keeps
   * the drawer body scrolling and the Download button pinned and visible.
   * TODO: remove once the panel hiding is fixed upstream in @joor/design-system.
   */
  & div:has(> .drawer) {
    overflow: clip;
    min-height: 0;
  }
`

/*
 * Slot for the Contentful CTA banner.
 *
 * Container is a flex column with a fixed height, so its children shrink to fit
 * the viewport. 'flex-shrink: 0' keeps the banner at its natural height and lets
 * the order content below absorb it instead, which keeps the page free of the
 * outer scrollbar the fixed height is there to avoid.
 *
 * <CTABanner> renders null when Contentful has no banner for this page, leaving
 * this slot as a zero-height flex item: no gap, no placeholder.
 */
const BannerSlot = styled.div`
  flex-shrink: 0;
`

const HeaderContainer = styled.div`
  z-index: 3;
`

const NavbarContainer = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  height: 54px;
  padding: 0 20px;

  & > * {
    box-shadow: none;
    background-color: white;
    gap: 24px;
  }
`

const FakeTabsWrapper = styled.div`
  display: flex;
  padding-left: 8px;
`

const FakeTab = styled.button`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 32px;
  line-height: 16px;
  margin: 0 12px;
  overflow: hidden;
  padding: 12px 0 8px 0;
  text-align: center;
  ${typography.alpha.primaryInactive.body1};
  color: var(--color-primary-800);
  background: var(--color-neutral-000-full);
  border: none;
  border-bottom: 4px solid var(--color-neutral-000-full);
  :focus {
    outline: none;
  }
  :hover,
  :focus {
    &:after {
      transform: scaleX(1);
    }
  }

  &.active {
    cursor: default;
    ${typography.alpha.primaryActive.subtitle3};
    border-bottom: 4px solid var(--color-secondary-800);
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  margin-right: 20px;
`

export default OrderReview
